import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Space, Statistic, Row, Col, Tooltip, Badge, Input, Tabs, Descriptions, Spin, Select } from 'antd';
import { LaptopOutlined, WifiOutlined, SearchOutlined } from '@ant-design/icons';
import { accountApi, clientApi, tradingApi } from '../services/api';
import { useAuth } from '../utils/auth';
import { useSearchParams } from 'react-router-dom';

// 客户端查看（只读）：复制自 AccountsPage，去掉所有同步 / 操作 / 刷新 / 编辑 / 交易 / 删除按钮
export default function ClientViewPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin' || user?.is_super_admin === 1;
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [todayOrders, setTodayOrders] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [filterStatus, setFilterStatus] = useState('online');
  const [clientSearch, setClientSearch] = useState('');
  const [fills, setFills] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPage, setClientPage] = useState({ current: 1, pageSize: 10 });
  const [positions, setPositions] = useState([]);
  const [apiDown, setApiDown] = useState(false);
  const [activeTab, setActiveTab] = useState('positions');
  const [quoteMap, setQuoteMap] = useState({});
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [orderDirection, setOrderDirection] = useState('');
  const [fillSearch, setFillSearch] = useState('');
  const [fillDirection, setFillDirection] = useState('');

  useEffect(() => {
    loadData();
    const cid = searchParams.get('client_id');
    if (cid) {
      setClientSearch(cid);
      setFilterStatus('');
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedClient) {
      loadAccounts();
      loadPositions();
      loadOrders();
      loadFills();
      loadTodayOrders();
      loadScreenshots();
    }
  }, [selectedClient]);

  const loadTodayOrders = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await clientApi.getTodayOrders(cid);
      if (res.success) setTodayOrders(res.data.today_orders || []);
    } catch { /* ignore */ }
  };

  const loadScreenshots = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await clientApi.getScreenshots(cid);
      if (res.success) setScreenshots(res.data || []);
    } catch { /* ignore */ }
  };

  const onTabChange = (key) => {
    setActiveTab(key);
    if (key === 'todayOrders') loadTodayOrders();
    else if (key === 'screenshots') loadScreenshots();
    else if (key === 'orders') loadOrders();
    else if (key === 'fills') loadFills();
    else if (key === 'positions') loadPositions();
    else if (key === 'accounts') loadAccounts();
  };

  const loadData = async () => {
    try {
      const clientRes = await clientApi.getOnline();
      if (clientRes.success) { setClients(clientRes.data || []); setApiDown(false); }
    } catch { setApiDown(true); }
  };

  const loadPositions = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await accountApi.getAllPositions({ client_id: cid });
      if (res.success) {
        const data = res.data || [];
        setPositions(data);
        const codes = [...new Set(data.map(p => p.stock_code).filter(Boolean).map(c => String(c).padStart(6, '0')))];
        if (codes.length > 0) {
          tradingApi.getQuotes(codes).then(qres => {
            if (qres.success) {
              const m = {};
              (qres.data || []).forEach(q => { if (q.stock_code) m[q.stock_code] = q; });
              setQuoteMap(m);
            }
          }).catch(() => {});
        }
      }
    } catch { /* ignore */ }
  };

  const loadAccounts = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await accountApi.getAll({ client_id: cid });
      if (res.success) setAccounts(res.data || []);
    } catch { /* ignore */ }
  };

  const loadOrders = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await tradingApi.getOrders({ client_id: cid, pageSize: 500 });
      if (res.success) setOrders(res.data.orders || []);
    } catch { /* ignore */ }
  };

  const loadFills = async () => {
    try {
      const cid = selectedClient?.id;
      if (!cid) return;
      const res = await tradingApi.getFills({ client_id: cid, pageSize: 500 });
      if (res.success) setFills(res.data?.fills || []);
    } catch { /* ignore */ }
  };

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (orderSearch) {
      const s = orderSearch.toLowerCase();
      list = list.filter(r => (r.stock_code || '').toLowerCase().includes(s) || (r.stock_name || '').toLowerCase().includes(s));
    }
    if (orderStatus) list = list.filter(r => r.status === orderStatus);
    if (orderDirection) list = list.filter(r => r.direction === orderDirection);
    return list;
  }, [orders, orderSearch, orderStatus, orderDirection]);

  const filteredFills = useMemo(() => {
    let list = fills;
    if (fillSearch) {
      const s = fillSearch.toLowerCase();
      list = list.filter(r => (r.stock_code || '').toLowerCase().includes(s) || (r.stock_name || '').toLowerCase().includes(s));
    }
    if (fillDirection) list = list.filter(r => r.direction === fillDirection);
    return list;
  }, [fills, fillSearch, fillDirection]);

  const clientColumns = [
    { title: '名称', dataIndex: 'name', width: 130, render: (v, r) => (
      <span><code style={{ fontSize: 11, marginRight: 6 }}>{r.id}</code><span>{v}</span></span>
    )},
    { title: '券商', dataIndex: 'broker', width: 80, render: v => v ? <Tag>{v}</Tag> : '-' },
    { title: '状态', dataIndex: 'status', width: 65, render: v =>
      v === 'online' ? <Tag color="green">在线</Tag> : <Tag color="default">离线</Tag>
    },
    { title: '同花顺', width: 70, render: (_, r) => {
      if (r.ths_status === 'ready') return <Tag color="green">就绪</Tag>;
      if (r.ths_status === 'disconnected') return <Tag color="red">未连接</Tag>;
      return r.status === 'online' ? <Tag color="default">等待中</Tag> : <Tag color="default">离线</Tag>;
    }},
    { title: '版本', dataIndex: 'version', width: 55, render: v => v ? <Tag>{v}</Tag> : '-' },
    { title: 'MAC', dataIndex: 'mac_addr', width: 120, render: v => v ? <code style={{ fontSize: 11 }}>{v}</code> : '-' },
  ];

  const accountColumns = [
    { title: '名称', dataIndex: 'name', width: 90, render: v => <span>{v}</span> },
    { title: '资金账号', dataIndex: 'account_no', width: 110, render: v => <span>{v}</span> },
    { title: '总资产', dataIndex: 'total_asset', width: 100, render: v => <span style={{ fontWeight: 600 }}>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { title: '可用金额', dataIndex: 'available', width: 90, render: v => <span style={{ color: '#27ae60' }}>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { title: '股票市值', dataIndex: 'market_value', width: 100, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
    { title: '冻结', dataIndex: 'frozen', width: 70, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
  ];

  const orderColumns = [
    { title: '日期', dataIndex: 'order_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'order_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向', dataIndex: 'direction', width: 50, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '价格', dataIndex: 'price', width: 65, render: v => v?.toFixed(2) || '市价' },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '成交', dataIndex: 'filled_qty', width: 65, render: v => v?.toLocaleString() || 0 },
    { title: '状态', dataIndex: 'status', width: 65, render: v => {
      const colorMap = { '已成': 'success', '已撤': 'default', '已报': 'processing', '部成': 'warning', '废单': 'error' };
      const color = Object.entries(colorMap).find(([k]) => v?.includes(k))?.[1] || 'default';
      return <Tag color={color}>{v || '-'}</Tag>;
    }},
    { title: '市场', dataIndex: 'market', width: 65, render: v => v ? <Tag>{v}</Tag> : '-' },
  ];

  const posColumns = [
    { title: '股票', key: 'stock', width: 120, render: (_, r) => {
      const gray = (r.quantity || 0) === 0;
      return <span style={{ color: gray ? '#ccc' : undefined }}><strong>{r.stock_name}</strong><br /><small>{r.stock_code}</small></span>;
    }},
    { title: '股票余额', dataIndex: 'quantity', width: 80, render: v => v?.toLocaleString() },
    { title: '可用余额', dataIndex: 'available_qty', width: 80, render: v => v?.toLocaleString() },
    { title: '冻结', dataIndex: 'frozen_qty', width: 60, render: v => v?.toLocaleString() || 0 },
    { title: '成本价', dataIndex: 'cost_price', width: 80, render: v => v?.toFixed(3) },
    { title: '市价', key: 'live_price', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      if (q) {
        const color = q.change_percent > 0 ? '#e74c3c' : q.change_percent < 0 ? '#27ae60' : '#333';
        return <span style={{ fontWeight: 600, color }}>{q.current_price.toFixed(2)}</span>;
      }
      return r.current_price?.toFixed(2) || '-';
    }},
    { title: '市值', dataIndex: 'market_value', width: 90, render: v => v?.toLocaleString() },
    { title: '参考盈亏', key: 'live_pl', width: 100, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      const qty = r.quantity || 0;
      const pl = cost > 0 ? (price - cost) * qty : (r.profit_loss || 0);
      return (
        <span style={{ color: pl >= 0 ? '#e74c3c' : '#27ae60', fontWeight: pl !== 0 ? 600 : 'normal' }}>
          {pl >= 0 ? '+' : ''}{pl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      );
    }},
    { title: '盈亏比例', key: 'live_plr', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      if (cost > 0 && q) {
        const ratio = (price - cost) / cost * 100;
        return <span style={{ color: ratio >= 0 ? '#e74c3c' : '#27ae60' }}>{ratio >= 0 ? '+' : ''}{ratio.toFixed(2)}%</span>;
      }
      return r.profit_loss_ratio ? `${r.profit_loss_ratio.toFixed(2)}%` : '-';
    }},
    { title: '交易市场', dataIndex: 'market', width: 70, render: v => v ? <Tag>{v}</Tag> : '-' },
  ];

  const fillColumns = [
    { title: '日期', dataIndex: 'trade_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'trade_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向', dataIndex: 'direction', width: 50, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '成交价', dataIndex: 'price', width: 65, render: v => v?.toFixed(2) },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '金额', dataIndex: 'amount', width: 90, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
    { title: '市场', dataIndex: 'market', width: 65, render: v => v ? <Tag>{v}</Tag> : '-' },
  ];

  const filteredClients = useMemo(() => {
    let list = clients;
    if (filterStatus) list = list.filter(c => c.status === filterStatus);
    if (clientSearch) {
      const s = clientSearch.toLowerCase();
      list = list.filter(c => (c.id || '').toLowerCase().includes(s) || (c.name || '').toLowerCase().includes(s));
    }
    return list;
  }, [clients, filterStatus, clientSearch]);

  const [assetStats, setAssetStats] = useState({ total_asset: 0, total_balance: 0, total_market_value: 0, total_available: 0, total_pl: 0 });
  const loadAssetStats = () => {
    accountApi.getAssetStats().then(r => { if (r.success) setAssetStats(r.data || {}); }).catch(() => {});
  };
  useEffect(loadAssetStats, []);

  const onlineCount = clients.filter(c => c.status === 'online').length;
  const totalAsset = assetStats.total_asset || 0;
  const totalMarketValue = assetStats.total_market_value || 0;
  const totalPL = assetStats.total_pl || 0;

  const directionOptions = [
    { label: '全部方向', value: '' },
    { label: '买入', value: 'buy' },
    { label: '卖出', value: 'sell' },
  ];
  const statusOptions = [
    { label: '全部状态', value: '' },
    { label: '已提交', value: 'submitted' },
    { label: '已成', value: 'filled' },
    { label: '部分', value: 'partially_filled' },
    { label: '已撤', value: 'cancelled' },
    { label: '拒单', value: 'rejected' },
  ];

  const tabItems = [
    { key: 'accounts', label: `券商账号 (${accounts.length})`, children: (
      <Table dataSource={accounts} columns={accountColumns} rowKey="id" size="small" pagination={false} locale={{ emptyText: '暂无账户数据' }} />
    )},
    { key: 'positions', label: `当前持仓 (${positions.length})`, children: (
      <Table dataSource={positions} columns={posColumns} rowKey="id" size="small" pagination={false}
        locale={{ emptyText: '暂无持仓数据' }} />
    )},
    { key: 'todayOrders', label: `当日委托 (${todayOrders.length})`, children: (
      <Table dataSource={todayOrders} columns={orderColumns} rowKey="id" size="small" pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }} scroll={{ y: 350 }}
        locale={{ emptyText: '暂无当日委托' }} />
    )},
    { key: 'screenshots', label: `截图记录 (${screenshots.length})`, children: (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 4 }}>
        {screenshots.map(s => (
          <div key={s.id} style={{ textAlign: 'center' }}>
            <div style={{ cursor: 'pointer' }}
              onClick={() => window.open(`/screenshots/file/${s.id}`, '_blank')}>
              <img src={`/screenshots/file/${s.id}`} alt="截图" style={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
            </div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{new Date(s.created_at).toLocaleString()}</div>
          </div>
        ))}
        {screenshots.length === 0 && <div style={{ color: '#999', padding: 40 }}>暂无截图记录</div>}
      </div>
    )},
    { key: 'orders', label: `历史委托 (${filteredOrders.length})`, children: (
      <div>
        <Space style={{ marginBottom: 8 }} wrap>
          <Input.Search placeholder="搜索股票" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} allowClear style={{ width: 130 }} />
          <Select value={orderDirection} onChange={setOrderDirection} options={directionOptions} style={{ width: 85 }} />
          <Select value={orderStatus} onChange={setOrderStatus} options={statusOptions} style={{ width: 85 }} />
        </Space>
        <Table dataSource={filteredOrders} columns={orderColumns} rowKey="id" size="small" pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }} scroll={{ y: 350 }} />
      </div>
    )},
    { key: 'fills', label: `历史成交 (${filteredFills.length})`, children: (
      <div>
        <Space style={{ marginBottom: 8 }} wrap>
          <Input.Search placeholder="搜索股票" value={fillSearch} onChange={e => setFillSearch(e.target.value)} allowClear style={{ width: 130 }} />
          <Select value={fillDirection} onChange={setFillDirection} options={directionOptions} style={{ width: 85 }} />
        </Space>
        <Table dataSource={filteredFills} columns={fillColumns} rowKey="id" size="small" pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }} scroll={{ y: 350 }} />
      </div>
    )},
  ];

  return (
    <div>
      {apiDown && (
        <Card size="small" style={{ marginBottom: 12, borderLeft: '3px solid #faad14' }}>
          <Space><Badge status="warning" /> 后端服务连接异常，正在自动重连...</Space>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col span={4}><Card><Statistic title="客户端在线" value={onlineCount} suffix={`/ ${clients.length}`} prefix={<LaptopOutlined />} valueStyle={{ color: onlineCount > 0 ? '#52c41a' : '#999' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="总资产" value={totalAsset} precision={2} suffix="元" valueStyle={{ color: '#2980b9' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="可用资金" value={assetStats.total_available || 0} precision={2} suffix="元" valueStyle={{ color: '#27ae60' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="持仓总市值" value={totalMarketValue} precision={2} suffix="元" valueStyle={{ color: '#e67e22' }} /></Card></Col>
        {isAdmin && <Col span={5}><Card><Statistic title="总持仓盈亏" value={totalPL} precision={2} suffix="元" valueStyle={{ color: totalPL >= 0 ? '#e74c3c' : '#27ae60' }} /></Card></Col>}
      </Row>

      <Card
        title={<Space><LaptopOutlined /> 客户端列表（只读）<Tag>在线 {onlineCount}/{clients.length}</Tag></Space>}
        size="small" style={{ marginTop: 12 }}
      >
        <Tabs size="small" activeKey={filterStatus || 'all'} onChange={k => setFilterStatus(k === 'all' ? '' : k)} items={[
          { key: 'all', label: `全部 (${clients.length})` },
          { key: 'online', label: `在线 (${clients.filter(c => c.status === 'online').length})` },
          { key: 'offline', label: `离线 (${clients.filter(c => c.status !== 'online').length})` },
        ]} />
        <Input.Search placeholder="搜索客户端名称或ID" value={clientSearch} onChange={e => setClientSearch(e.target.value)}
          style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
        <Table
          dataSource={filteredClients} columns={clientColumns} rowKey="id" size="small"
          pagination={{
            current: clientPage.current,
            pageSize: clientPage.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: t => `共 ${t} 台`,
            onChange: (current, pageSize) => setClientPage({ current, pageSize }),
          }}
          locale={{ emptyText: '暂无客户端' }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedClient ? [selectedClient.id] : [],
            onChange: (keys, rows) => { if (rows.length > 0) setSelectedClient(rows[0]); },
          }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                <Descriptions size="small" column={3}>
                  <Descriptions.Item label="客户端ID"><code>{r.id}</code></Descriptions.Item>
                  <Descriptions.Item label="客户端名称">{r.name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="券商">{r.broker || '-'}</Descriptions.Item>
                  <Descriptions.Item label="远程桌面账号">{r.sunflower_account || '-'}</Descriptions.Item>
                  <Descriptions.Item label="远程桌面密码">{r.sunflower_pass || '-'}</Descriptions.Item>
                  <Descriptions.Item label="MAC 地址">{r.mac_addr || '-'}</Descriptions.Item>
                  <Descriptions.Item label="机器ID">{r.machine_id ? <code style={{ fontSize: 11 }}>{r.machine_id}</code> : '-'}</Descriptions.Item>
                  <Descriptions.Item label="操作系统">{r.os_version || '-'}</Descriptions.Item>
                  <Descriptions.Item label="CPU">{r.cpu_info || '-'}</Descriptions.Item>
                  <Descriptions.Item label="内存">{r.memory_total || '-'}</Descriptions.Item>
                </Descriptions>
              </div>
            ),
            rowExpandable: () => true,
          }}
        />
      </Card>

      <Card
        title={selectedClient
          ? <Space><Badge status={selectedClient.status === 'online' ? 'success' : 'default'} /> {selectedClient.name} - 详情</Space>
          : <span style={{ color: '#999' }}>请选择一个客户端查看详情</span>}
        size="small" style={{ marginTop: 12 }} bodyStyle={{ padding: selectedClient ? 0 : 24 }}
      >
        {selectedClient ? (
          <Tabs activeKey={activeTab} onChange={onTabChange} items={tabItems} size="small" style={{ margin: '0 8px' }} />
        ) : (
          <div style={{ textAlign: 'center', color: '#999' }}>点击上方客户端列表选中设备</div>
        )}
      </Card>
    </div>
  );
}
